import React, { useEffect } from 'react';
import BlogLayout from '../../components/BlogLayout';

export default function TheBuildDossier() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <BlogLayout
            title="The Build Dossier: What We Prepare Before Your RoofClaw Agent Ships"
            date="March 6, 2026"
            readTime="4 MIN"
            heroImage="/images/blog/the-build-dossier-hero.png"
            description="Before your RoofClaw agent ships, we build a complete dossier — custom config files, your knowledge base, your credentials, and your agent's full identity. Here's what's inside."
        >
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Most AI deployments are improvised.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Someone buys access to a tool, gets a login, watches a YouTube tutorial, and figures it out from there. The configuration is whatever they managed to type into a prompt window on day one. The setup is whatever held together through trial and error.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That's fine for a hobby. It's not fine for a business.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                When a RoofClaw ships to a client, the agent inside doesn't just wake up and start guessing. It knows who it is. It knows who you are. It knows your business, your tools, your priorities, your voice. It has your documents. It has your credentials staged and ready. The agent's entire context — everything it needs to be useful on day one — is built before the box arrives.
            </p>

            <p className="mb-12 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                We call it the Build Dossier.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The problem with "configure as you go"</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Here's what usually happens with AI tools: you get it live, then spend the first three weeks teaching it things it should have known on arrival.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                What's my company name? What CRM do I use? How should I talk to customers? What are my top priorities?
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Every session, the agent starts from scratch unless you're manually re-entering context. And even then, it's inconsistent.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                We decided early on that this wasn't acceptable for a product we stand behind. The onboarding experience — the gap between "I paid" and "this is actually running my business" — was the highest-risk moment for client success.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The Build Dossier is our answer to that problem.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What's actually in the dossier</h2>

            <p className="mb-8 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Every RoofClaw client gets a custom package built before their agent ships. It contains six core configuration files and everything the agent needs to operate without guessing.
            </p>

            <div className="space-y-4 mb-12">
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="font-data text-xs text-[#7B61FF] tracking-wider mb-2">SOUL.md</p>
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">
                        The agent's identity. Its name, role, personality, communication style, how much it pushes back, what it values. Written from scratch for every client based on their intake interview with Adam. No two are the same.
                    </p>
                </div>

                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="font-data text-xs text-[#7B61FF] tracking-wider mb-2">USER.md</p>
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">
                        This is who the agent is working for. You. Your name, how you like to be addressed, your timezone, your working style, what you care about, what you can't stand. The agent reads this and knows who's on the other side of every conversation.
                    </p>
                </div>

                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="font-data text-xs text-[#7B61FF] tracking-wider mb-2">IDENTITY.md</p>
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">
                        The agent's card. Name, title, email address, contact info. The building blocks of how it presents itself to the world.
                    </p>
                </div>

                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="font-data text-xs text-[#7B61FF] tracking-wider mb-2">AGENTS.md + HEARTBEAT.md</p>
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">
                        Operational context. How the agent runs, what it checks proactively, when to surface issues versus handle them silently.
                    </p>
                </div>

                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="font-data text-xs text-[#7B61FF] tracking-wider mb-2">TOOLS.md</p>
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">
                        Your tech stack. Your CRM. Your project management tool. Your communication platforms. The agent knows what it has access to and how to use it.
                    </p>
                </div>
            </div>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                On top of the config files, the dossier includes your credentials (staged securely and ready to install), any documents you've shared during onboarding — process guides, templates, client-facing materials — and the build notes that Claudio uses during the actual provisioning session. Everything in one place. Nothing improvised.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The knowledge base</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                This one surprised clients when we described it.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Over the course of onboarding, you share things. An intake form. An email with a PDF about how your estimating process works. A note about a specific customer situation you want the agent to handle carefully.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                We file all of it.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The Build Dossier includes a Knowledge Base section — every document, every context note, every link you've shared — organized and tagged for the build. When the agent ships, it arrives with institutional knowledge about your business. Not because it's "smart." Because we did the work to give it that knowledge before day one.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Documents flagged as "include in build" get bundled directly into the package. The agent starts informed.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The build moment</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                When all pre-reqs are complete, credentials are captured, config files are reviewed, and the Knowledge Base is organized — Adam reviews the dossier in the admin dashboard and clicks one button: <span className="text-[#F0EFF4] font-bold">Mark Ready to Build.</span>
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That's the gate. No agent ships without a human reviewing the full package. Adam is the last set of eyes before Claudio begins the provisioning session.
            </p>

            <div className="bg-[#0A0A14] border border-[#10B981]/20 rounded-2xl p-8 my-10">
                <p className="font-data text-xs text-[#10B981] tracking-wider mb-4">THE POINT OF THE GATE</p>
                <p className="text-[#F0EFF4] text-xl font-bold mb-3">The 3-hour delivery session isn't troubleshooting.</p>
                <p className="text-[#F0EFF4]/70 text-base leading-relaxed">
                    It's activation. Because the work happened before you got on the call.
                </p>
            </div>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">Why this matters</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                We talk a lot about what RoofClaw does after it ships. The 24/7 availability. The CRM integrations. The autonomous admin work.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                But the thing clients notice first is how it shows up on day one.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                They get on the delivery call and the agent already knows their company name, their top three priorities, their preferred communication style. It introduces itself correctly. It handles the first test question without hesitation.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That's not magic. That's the dossier.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                We built this process because we believe the gap between "tool" and "employee" is almost entirely determined by what happens before the first conversation. A tool needs constant setup. An employee arrives prepared.
            </p>

            <p className="mb-12 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                RoofClaw ships prepared.
            </p>

            <hr className="my-16 border-[#F0EFF4]/10" />

            <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-[2rem] p-10 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-64 h-64 bg-[#7B61FF] opacity-10 blur-[100px] rounded-full mix-blend-screen pointer-events-none"></div>
                <h2 className="text-3xl font-heading font-bold mb-6">Want to see what your dossier looks like?</h2>
                <p className="text-lg text-[#F0EFF4]/80 mb-6">
                    Every build starts with a 20-minute intake call. We'll walk you through exactly what gets configured before your agent ships.
                </p>
                <a
                    href="https://roofclaw.com/#contact"
                    className="inline-flex items-center gap-2 bg-[#7B61FF] text-white font-bold px-8 py-4 rounded-full hover:bg-[#6B51EF] transition-colors"
                >
                    Book a 20-minute call →
                </a>
            </div>
        </BlogLayout>
    );
}
